import React, { useState, useMemo, useEffect, useCallback } from 'react';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import {
  GlassCard, Button, Badge, SectionHeader, Divider, StatCard,
} from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

// ─── Types ──────────────────────────────────────────────────

interface MonthlyStat {
  month: string;
  revenue: number;
  collected: number;
  commission: number;
  salesCount: number;
}

interface AgentPerformance {
  name: string;
  totalSales: number;
  revenue: number;
  commission: number;
  collectionRate: number;
}

interface SectionStat {
  sectionId: string;
  totalLots: number;
  sold: number;
  reserved: number;
  available: number;
  revenue: number;
}

// ─── Mock Data ──────────────────────────────────────────────

const MONTHLY_DATA: MonthlyStat[] = [
  { month: '2024-01', revenue: 620000, collected: 480000, commission: 31000, salesCount: 4 },
  { month: '2024-02', revenue: 485000, collected: 320000, commission: 24250, salesCount: 3 },
  { month: '2024-03', revenue: 780000, collected: 580000, commission: 39000, salesCount: 5 },
  { month: '2024-04', revenue: 340000, collected: 250000, commission: 17000, salesCount: 2 },
  { month: '2024-05', revenue: 920000, collected: 710000, commission: 46000, salesCount: 6 },
  { month: '2024-06', revenue: 560000, collected: 420000, commission: 28000, salesCount: 4 },
  { month: '2024-07', revenue: 1050000, collected: 820000, commission: 52500, salesCount: 7 },
  { month: '2024-08', revenue: 720000, collected: 560000, commission: 36000, salesCount: 5 },
  { month: '2024-09', revenue: 830000, collected: 640000, commission: 41500, salesCount: 5 },
  { month: '2024-10', revenue: 1150000, collected: 900000, commission: 57500, salesCount: 8 },
  { month: '2024-11', revenue: 660000, collected: 510000, commission: 33000, salesCount: 4 },
  { month: '2024-12', revenue: 890000, collected: 710000, commission: 44500, salesCount: 6 },
];

const PREV_YEAR_DATA: MonthlyStat[] = [
  { month: '2023-01', revenue: 480000, collected: 360000, commission: 24000, salesCount: 3 },
  { month: '2023-02', revenue: 390000, collected: 290000, commission: 19500, salesCount: 2 },
  { month: '2023-03', revenue: 610000, collected: 440000, commission: 30500, salesCount: 4 },
  { month: '2023-04', revenue: 280000, collected: 200000, commission: 14000, salesCount: 2 },
  { month: '2023-05', revenue: 750000, collected: 560000, commission: 37500, salesCount: 5 },
  { month: '2023-06', revenue: 420000, collected: 310000, commission: 21000, salesCount: 3 },
  { month: '2023-07', revenue: 830000, collected: 620000, commission: 41500, salesCount: 5 },
  { month: '2023-08', revenue: 580000, collected: 430000, commission: 29000, salesCount: 4 },
  { month: '2023-09', revenue: 690000, collected: 510000, commission: 34500, salesCount: 4 },
  { month: '2023-10', revenue: 920000, collected: 720000, commission: 46000, salesCount: 6 },
  { month: '2023-11', revenue: 530000, collected: 400000, commission: 26500, salesCount: 3 },
  { month: '2023-12', revenue: 710000, collected: 540000, commission: 35500, salesCount: 5 },
];

// Agent, section, funnel, and overdue data fetched from backend API

// ─── Helpers ────────────────────────────────────────────────

const fmt = (n: number) => `₱${n.toLocaleString()}`;
const pct = (a: number, b: number) => b === 0 ? '0%' : `${Math.round((a / b) * 100)}%`;
const monthLabel = (m: string) => new Date(m + '-01').toLocaleDateString('en-PH', { month: 'short' });

// ─── Component ──────────────────────────────────────────────

const Reports: React.FC = () => {
  const { addToast } = useToast();
  const [dateRange, setDateRange] = useState<'ytd' | 'q1' | 'q2' | 'q3' | 'q4' | 'all'>('all');
  const [compareYoY, setCompareYoY] = useState(true);

  // API data
  const [agentData, setAgentData] = useState<AgentPerformance[]>([]);
  const [sectionData, setSectionData] = useState<SectionStat[]>([]);
  const [funnelData, setFunnelData] = useState<{ stage: string; count: number; color: string }[]>([]);
  const [overduePayments, setOverduePayments] = useState<{ client: string; lot: string; amount: number; daysOverdue: number }[]>([]);
  const [apiKpi, setApiKpi] = useState<{ totalRevenue: number; totalCollected: number; totalCommission: number; totalSales: number } | null>(null);

  const fetchReports = useCallback(async () => {
    try {
      const data = await api.reports.summary();
      setAgentData(data.agentPerformance || []);
      setSectionData(data.sectionStats || []);
      setFunnelData(data.funnelData || []);
      setOverduePayments(data.overduePayments || []);
      setApiKpi(data.kpi || null);
    } catch {
      // fallback to zeros
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Filtered data
  const filteredData = useMemo(() => {
    if (dateRange === 'all') return MONTHLY_DATA;
    if (dateRange === 'ytd') {
      const currentMonth = new Date().getMonth();
      return MONTHLY_DATA.slice(0, currentMonth + 1);
    }
    const qMap: Record<string, [number, number]> = { q1: [0, 3], q2: [3, 6], q3: [6, 9], q4: [9, 12] };
    const [start, end] = qMap[dateRange] || [0, 12];
    return MONTHLY_DATA.slice(start, end);
  }, [dateRange]);

  const totalRevenue = apiKpi?.totalRevenue ?? filteredData.reduce((s, m) => s + m.revenue, 0);
  const totalCollected = apiKpi?.totalCollected ?? filteredData.reduce((s, m) => s + m.collected, 0);
  const totalCommission = apiKpi?.totalCommission ?? filteredData.reduce((s, m) => s + m.commission, 0);
  const totalSales = apiKpi?.totalSales ?? filteredData.reduce((s, m) => s + m.salesCount, 0);

  const prevYearRevenue = PREV_YEAR_DATA.reduce((s, m) => s + m.revenue, 0);
  const yoyGrowth = prevYearRevenue > 0 ? Math.round(((totalRevenue - prevYearRevenue) / prevYearRevenue) * 100) : 0;

  const maxRevenue = Math.max(...MONTHLY_DATA.map(m => m.revenue));

  // CSV export
  const exportCSV = (data: Record<string, any>[], filename: string) => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h]}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    addToast('success', 'Export Complete', `${filename}.csv downloaded.`);
  };

  // ─── Bar Chart (pure CSS) ─────────────────────────────

  const renderBarChart = () => (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '180px', padding: '0 4px' }}>
      {MONTHLY_DATA.map((m, i) => {
        const h = (m.revenue / maxRevenue) * 100;
        const prevH = PREV_YEAR_DATA[i] ? (PREV_YEAR_DATA[i].revenue / maxRevenue) * 100 : 0;
        const isFiltered = filteredData.includes(m);
        return (
          <div key={m.month} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '2px', position: 'relative' }}>
            {compareYoY && (
              <div style={{
                flex: 1, height: `${prevH}%`, borderRadius: '4px 4px 0 0',
                background: 'rgba(148,163,184,0.12)', transition: 'height 0.4s ease',
                minHeight: '2px',
              }} />
            )}
            <div style={{
              flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0',
              background: isFiltered
                ? 'linear-gradient(180deg, rgba(13,148,136,0.80), rgba(13,148,136,0.40))'
                : 'rgba(148,163,184,0.12)',
              transition: 'height 0.4s ease', minHeight: '2px',
            }} />
            <div style={{
              position: 'absolute', bottom: '-20px', left: '50%', transform: 'translateX(-50%)',
              fontSize: '10px', color: 'var(--text-faint)', whiteSpace: 'nowrap',
            }}>
              {monthLabel(m.month)}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <TopBar title="Reports" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden',
            border: '1px solid var(--glass-border)',
          }}>
            {(['all', 'ytd', 'q1', 'q2', 'q3', 'q4'] as const).map(r => (
              <button key={r} onClick={() => setDateRange(r)} style={{
                padding: '8px 14px', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer',
                background: dateRange === r ? 'rgba(13,148,136,0.15)' : 'var(--input-bg)',
                color: dateRange === r ? 'var(--primary-400)' : 'var(--text-secondary)',
                fontFamily: 'var(--font-body)', transition: 'all 0.15s ease',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{r}</button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={compareYoY} onChange={() => setCompareYoY(!compareYoY)}
              style={{ accentColor: 'var(--primary-600)' }} />
            Compare YoY
          </label>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" size="sm" icon="solar:export-bold-duotone"
            onClick={() => exportCSV(MONTHLY_DATA.map(m => ({
              Month: m.month, Revenue: m.revenue, Collected: m.collected,
              Commission: m.commission, Sales: m.salesCount,
            })), 'monthly-report')}>
            Export CSV
          </Button>
        </div>

        {/* KPI Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <StatCard label="Total Revenue" value={fmt(totalRevenue)} icon="solar:wallet-money-bold-duotone"
            trend={`${yoyGrowth >= 0 ? '+' : ''}${yoyGrowth}% YoY`} trendUp={yoyGrowth >= 0} />
          <StatCard label="Total Collected" value={fmt(totalCollected)} icon="solar:hand-money-bold-duotone"
            iconBg="rgba(34,197,94,0.12)" iconColor="#22C55E"
            trend={`${pct(totalCollected, totalRevenue)} rate`} trendUp={totalCollected / totalRevenue > 0.7} />
          <StatCard label="Total Commission" value={fmt(totalCommission)} icon="solar:chart-2-bold-duotone"
            iconBg="rgba(59,130,246,0.12)" iconColor="#3B82F6" />
          <StatCard label="Total Sales" value={String(totalSales)} icon="solar:sale-bold-duotone"
            iconBg="rgba(245,158,11,0.12)" iconColor="#F59E0B"
            trend={`~${Math.round(totalSales / filteredData.length)} per month`} trendUp={true} />
        </div>

        {/* Revenue Chart */}
        <GlassCard>
          <SectionHeader title="Monthly Revenue" icon="solar:chart-2-bold-duotone"
            actions={
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'rgba(13,148,136,0.60)' }} />
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>2024</span>
                </div>
                {compareYoY && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'rgba(148,163,184,0.12)' }} />
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>2023</span>
                  </div>
                )}
              </div>
            }
          />
          <div style={{ marginTop: '16px', paddingBottom: '28px' }}>
            {renderBarChart()}
          </div>
        </GlassCard>

        {/* Two-column row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

          {/* Agent Performance */}
          <GlassCard>
            <SectionHeader title="Agent Performance" icon="solar:user-bold-duotone"
              actions={
                <Button variant="ghost" size="sm" icon="solar:export-bold-duotone"
                  onClick={() => exportCSV(agentData.map((a: AgentPerformance) => ({
                    Agent: a.name, Sales: a.totalSales, Revenue: a.revenue,
                    Commission: a.commission, CollectionRate: a.collectionRate + '%',
                  })), 'agent-performance')}>
                  CSV
                </Button>
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              {(agentData.length > 0 ? agentData : []).map((agent, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)',
                }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(13,148,136,0.15)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'var(--primary-400)', fontWeight: 700, fontSize: '12px',
                  }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{agent.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{agent.totalSales} sales · {fmt(agent.commission)} comm.</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{fmt(agent.revenue)}</div>
                    <div style={{ fontSize: '11px', color: agent.collectionRate >= 80 ? 'var(--success)' : 'var(--warning)' }}>
                      {agent.collectionRate}% collected
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Client Acquisition Funnel */}
          <GlassCard>
            <SectionHeader title="Client Acquisition Funnel" icon="solar:filter-bold-duotone" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
              {(funnelData.length > 0 ? funnelData : []).map((item, i) => {
                const widthPct = funnelData.length > 0 ? (item.count / (funnelData[0]?.count || 1)) * 100 : 0;
                const convRate = i > 0 && funnelData[i - 1]?.count ? Math.round((item.count / funnelData[i - 1].count) * 100) : 100;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{item.stage}</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {i > 0 && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{convRate}% conv.</span>}
                        <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{item.count}</span>
                      </div>
                    </div>
                    <div style={{
                      height: '24px', borderRadius: 'var(--radius-sm)',
                      background: 'rgba(148,163,184,0.06)', overflow: 'hidden',
                      display: 'flex', justifyContent: 'center',
                    }}>
                      <div style={{
                        width: `${widthPct}%`, height: '100%', borderRadius: 'var(--radius-sm)',
                        background: `${item.color}40`, borderLeft: `3px solid ${item.color}`,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        </div>

        {/* Section Stats + Overdue Payments */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

          {/* Section/Lot Stats */}
          <GlassCard>
            <SectionHeader title="Inventory by Section" icon="solar:map-bold-duotone"
              actions={
                <Button variant="ghost" size="sm" icon="solar:export-bold-duotone"
                  onClick={() => exportCSV(sectionData.map((s: SectionStat) => ({
                    Section: s.sectionId, Total: s.totalLots, Sold: s.sold,
                    Reserved: s.reserved, Available: s.available, Revenue: s.revenue,
                  })), 'section-inventory')}>
                  CSV
                </Button>
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
              {(sectionData.length > 0 ? sectionData : []).map(sec => (
                <div key={sec.sectionId} style={{
                  padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>Section {sec.sectionId}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>{fmt(sec.revenue)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <Badge variant="available">{sec.available} avail.</Badge>
                    <Badge variant="reserved">{sec.reserved} res.</Badge>
                    <Badge variant="sold">{sec.sold} sold</Badge>
                  </div>
                  <div style={{ height: '6px', borderRadius: 'var(--radius-full)', background: 'rgba(148,163,184,0.10)', display: 'flex', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'var(--danger)', width: `${(sec.sold / sec.totalLots) * 100}%` }} />
                    <div style={{ height: '100%', background: 'var(--warning)', width: `${(sec.reserved / sec.totalLots) * 100}%` }} />
                    <div style={{ height: '100%', background: 'var(--success)', width: `${(sec.available / sec.totalLots) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Overdue Payments */}
          <GlassCard>
            <SectionHeader title="Overdue Payments" icon="solar:danger-triangle-bold-duotone" />
            {overduePayments.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                {overduePayments.map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.06)',
                    borderLeft: '3px solid var(--danger)',
                  }}>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{p.client}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Lot {p.lot} · {p.daysOverdue} days overdue</div>
                    </div>
                    <div style={{ color: 'var(--danger)', fontWeight: 700, fontSize: '14px' }}>{fmt(p.amount)}</div>
                  </div>
                ))}
                <Divider spacing="8px" />
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 14px' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600 }}>Total Overdue</span>
                  <span style={{ color: 'var(--danger)', fontWeight: 700, fontSize: '15px' }}>
                    {fmt(overduePayments.reduce((s, p) => s + p.amount, 0))}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--success)', fontSize: '14px', padding: '20px 0', textAlign: 'center' }}>
                <Icon icon="solar:check-circle-bold-duotone" width={24} style={{ marginBottom: '8px' }} />
                <div>All payments are up to date!</div>
              </div>
            )}
          </GlassCard>
        </div>

        {/* Forecast */}
        <GlassCard>
          <SectionHeader title="Revenue Forecast" icon="solar:graph-up-bold-duotone" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginTop: '8px' }}>
            {[
              { label: 'Projected Annual Revenue', value: fmt(totalRevenue * (12 / filteredData.length || 1)), sub: 'Based on current trend' },
              { label: 'Projected Commission', value: fmt(totalCommission * (12 / filteredData.length || 1)), sub: 'Based on current rates' },
              { label: 'Collection Forecast', value: pct(totalCollected, totalRevenue), sub: `${fmt(totalCollected)} of ${fmt(totalRevenue)}` },
            ].map((item, i) => (
              <div key={i} style={{
                padding: '16px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{item.label}</div>
                <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-heading)' }}>{item.value}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{item.sub}</div>
              </div>
            ))}
          </div>
        </GlassCard>

      </div>
    </>
  );
};

export default Reports;
